import { createRootRouteWithContext, Navigate, Outlet, useRouterState } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { useCurrentUser, isAdminRole } from '../lib/auth'
import { hasAuth } from '../lib/api'
import { useActivityTracker } from '../lib/activityTracker'
import { ForcePasswordChange } from '../components/ForcePasswordChange'
import { PreferencesProvider } from '../lib/preferences'
import { AppShell } from '../components/layout/AppShell'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useMessages } from '@/i18n/messages'
import { commonMessages } from '@/i18n/common'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

/** Routes reachable without a session. Everything else redirects to /login. */
const PUBLIC_PATHS = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/umsokn',
  '/umsokn/stadfesting',
])

function RootLayout() {
  const m = useMessages(commonMessages)
  const { data: user, isLoading } = useCurrentUser()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isPublic = PUBLIC_PATHS.has(pathname)
  useActivityTracker(Boolean(user) && !user?.mustChangePassword)

  // Waiting on /me while a token is present — avoid flashing the pre-auth chrome.
  if (isLoading && hasAuth()) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 text-sm text-slate-400">
        {m.loading}
      </div>
    )
  }

  // Not signed in: public pages render bare; everything else goes to login.
  if (!user) {
    return isPublic ? (
      <div className="min-h-screen bg-gray-50">
        <div className="flex justify-end px-4 pt-4">
          <LanguageSwitcher />
        </div>
        <Outlet />
      </div>
    ) : (
      <Navigate to="/login" />
    )
  }

  // Signed in but sitting on login/forgot/reset — send them home. The Hub
  // dashboard is admin-only, so regular users go straight to their customers.
  if (isPublic) {
    return <Navigate to={isAdminRole(user) ? '/' : '/customers'} />
  }

  // Blocking forced password change — gated route components never mount.
  if (user.mustChangePassword) {
    return <ForcePasswordChange user={user} />
  }

  return (
    <PreferencesProvider authed>
      <AppShell user={user}>
        <Outlet />
      </AppShell>
    </PreferencesProvider>
  )
}
